import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
const STAFF_ROLES = ['Founders', 'Developer', 'Surf Manager', 'GMOD Manager', 'Surf Admin', 'GMOD Admin'];
export default class StaffManagementPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.username = '';
    this.steamId64 = '';
    this.selectedRole = 'Surf Admin';
    this.reason = '';
    this.member = null;
    this.message = null;
    this.error = null;
    this.syncStatus = null;
    this.searching = false;
    this.saving = false;
    this.dashboard = null;
    this.dashboardLoading = false;
    this.dashboardError = null;
    this.gameRoles = null;
    this.gameRolesLoading = false;
    this.gameRolesSaving = false;
    this.gameRolesError = null;
    this.gameRolesMessage = null;
    this.newGameRole = '';
    this.selectedGameRole = 'Surf Admin';
    this.gameImmunity = 50;
    this.gamePermissions = [];
    this.permissionSearch = '';
    this.customPermission = '';
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    app.setTitle('Staff Management');
    app.setTitleCount(0);
    if (this.isOwner()) {
      this.loadDashboard();
      this.loadGameRoles();
    }
  }
  isOwner() {
    const user = app.session.user;
    const groups = user && typeof user.groups === 'function' ? user.groups() : [];
    return Boolean(user && (typeof user.isAdmin === 'function' && user.isAdmin() || Array.isArray(groups) && groups.some(group => {
      const name = typeof group.nameSingular === 'function' ? group.nameSingular() : group.nameSingular;
      return name === 'Founders';
    })));
  }
  clearAlerts() {
    this.message = null;
    this.error = null;
    this.syncStatus = null;
  }
  clearMember() {
    this.member = null;
    this.selectedRole = 'Surf Admin';
    this.reason = '';
    this.clearAlerts();
  }
  getErrorMessage(error, fallback) {
    try {
      if (error?.response?.message) {
        return error.response.message;
      }
      if (error?.response?.errors?.[0]?.detail) {
        return error.response.errors[0].detail;
      }
      if (typeof error?.responseText === 'string') {
        const response = JSON.parse(error.responseText);
        if (response?.message) {
          return response.message;
        }
        if (response?.errors?.[0]?.detail) {
          return response.errors[0].detail;
        }
      }
    } catch (parseError) {
      console.error(parseError);
    }
    return fallback;
  }
  loadDashboard() {
    if (this.dashboardLoading) {
      return;
    }
    this.dashboardLoading = true;
    this.dashboardError = null;
    app.request({
      method: 'GET',
      url: `${app.forum.attribute('baseUrl')}/hideout/staff/dashboard`
    }).then(response => {
      this.dashboard = response;
    }).catch(error => {
      console.error(error);
      this.dashboardError = this.getErrorMessage(error, 'The staff dashboard could not be loaded.');
    }).finally(() => {
      this.dashboardLoading = false;
      m.redraw();
    });
  }
  lookupMember(event) {
    event.preventDefault();
    if (this.searching || !this.username.trim() || this.steamId64.trim().length !== 17) {
      return;
    }
    this.member = null;
    this.reason = '';
    this.clearAlerts();
    this.searching = true;
    const params = new URLSearchParams({
      username: this.username.trim(),
      steamId64: this.steamId64.trim()
    });
    app.request({
      method: 'GET',
      url: `${app.forum.attribute('baseUrl')}/hideout/staff/lookup?` + params.toString()
    }).then(response => {
      this.member = response.user;
      this.selectedRole = response.user.currentRole || 'Surf Admin';
    }).catch(error => {
      console.error(error);
      this.error = this.getErrorMessage(error, 'No account matched both that exact username and verified SteamID64.');
    }).finally(() => {
      this.searching = false;
      m.redraw();
    });
  }
  assignRole() {
    if (!this.member || this.saving || this.reason.trim().length < 5 || this.selectedRole === this.member.currentRole) {
      return;
    }
    const confirmed = window.confirm(`Change ${this.member.username}'s staff role to ${this.selectedRole}?\n\nReason: ${this.reason.trim()}`);
    if (confirmed) {
      this.submitAssignment('assign');
    }
  }
  revokeRole() {
    if (!this.member || !this.member.currentRole || this.saving || this.reason.trim().length < 5) {
      return;
    }
    const confirmed = window.confirm(`Remove ${this.member.username}'s ${this.member.currentRole} role?\n\nReason: ${this.reason.trim()}`);
    if (confirmed) {
      this.submitAssignment('revoke');
    }
  }
  submitAssignment(action) {
    this.saving = true;
    this.clearAlerts();
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('baseUrl')}/hideout/staff/assignment`,
      body: {
        username: this.member.username,
        steamId64: this.member.steam.steamId64,
        role: this.selectedRole,
        action,
        reason: this.reason.trim()
      }
    }).then(response => {
      this.message = response.message;
      this.member.currentRole = response.role;
      this.syncStatus = response.sync || null;
      this.reason = '';
      if (!response.role) {
        this.selectedRole = 'Surf Admin';
      }
      this.loadDashboard();
    }).catch(error => {
      console.error(error);
      this.error = this.getErrorMessage(error, 'The staff assignment could not be changed. Please try again.');
    }).finally(() => {
      this.saving = false;
      m.redraw();
    });
  }
  loadGameRoles() {
    if (this.gameRolesLoading) {
      return;
    }
    this.gameRolesLoading = true;
    this.gameRolesError = null;
    app.request({
      method: 'GET',
      url: `${app.forum.attribute('baseUrl')}/hideout/staff/game-roles`
    }).then(response => {
      this.gameRoles = response;
      const availableRoles = response.roles || [];
      const selected = availableRoles.find(policy => policy.role === this.selectedGameRole) || availableRoles[0];
      if (selected) {
        this.selectGameRole(selected.role);
      }
    }).catch(error => {
      console.error(error);
      this.gameRolesError = this.getErrorMessage(error, 'Game rank permissions could not be loaded.');
    }).finally(() => {
      this.gameRolesLoading = false;
      m.redraw();
    });
  }
  selectGameRole(role) {
    const policy = (this.gameRoles?.roles || []).find(candidate => candidate.role === role);
    if (!policy) {
      return;
    }
    this.selectedGameRole = role;
    this.gameImmunity = policy.immunity;
    this.gamePermissions = [...(policy.permissions || [])];
    this.permissionSearch = '';
    this.customPermission = '';
    this.gameRolesError = null;
    this.gameRolesMessage = null;
  }
  createGameRole(event) {
    event.preventDefault();
    const role = this.newGameRole.trim();
    if (!role || this.gameRolesSaving) return;
    this.gameRolesSaving = true;
    this.gameRolesError = null;
    this.gameRolesMessage = null;
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('baseUrl')}/hideout/staff/game-roles`,
      body: {
        action: 'create_role',
        role
      }
    }).then(response => {
      this.gameRoles.roles = response.roles || this.gameRoles.roles;
      this.newGameRole = '';
      this.selectGameRole(response.policy.role);
      this.gameRolesMessage = response.message;
    }).catch(error => {
      console.error(error);
      this.gameRolesError = this.getErrorMessage(error, 'The game rank could not be created.');
    }).finally(() => {
      this.gameRolesSaving = false;
      m.redraw();
    });
  }
  hasGamePermission(permission) {
    return this.gamePermissions.includes(permission);
  }
  toggleGamePermission(permission) {
    if (this.gameRolesSaving) {
      return;
    }
    if (this.hasGamePermission(permission)) {
      this.gamePermissions = this.gamePermissions.filter(candidate => candidate !== permission);
    } else {
      this.gamePermissions = [...this.gamePermissions, permission];
    }
    this.gameRolesMessage = null;
  }
  addCustomGamePermission(event) {
    event.preventDefault();
    const permission = this.customPermission.trim();
    if (!permission || !/^[A-Za-z0-9*_.:-]+$/.test(permission) || this.isExcludedMenuPermission(permission)) {
      this.gameRolesError = 'Enter a valid permission key. Menu-plugin permissions are excluded.';
      return;
    }
    if (!this.hasGamePermission(permission)) {
      this.gamePermissions = [...this.gamePermissions, permission];
    }
    this.customPermission = '';
    this.gameRolesError = null;
    this.gameRolesMessage = null;
  }
  isExcludedMenuPermission(permission) {
    const namespace = permission.split(':', 1)[0].replace(/[^A-Za-z0-9]/g, '').toLowerCase();
    return namespace.endsWith('menu');
  }
  saveGameRole() {
    if (this.gameRolesSaving) {
      return;
    }
    const immunity = Number(this.gameImmunity);
    if (!Number.isInteger(immunity) || immunity < 0 || immunity > 255) {
      this.gameRolesError = 'Immunity must be a whole number between 0 and 255.';
      return;
    }
    this.gameRolesSaving = true;
    this.gameRolesError = null;
    this.gameRolesMessage = null;
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('baseUrl')}/hideout/staff/game-roles`,
      body: {
        role: this.selectedGameRole,
        immunity,
        permissions: this.gamePermissions
      }
    }).then(response => {
      this.gameRoles.roles = this.gameRoles.roles.map(policy => policy.role === response.policy.role ? response.policy : policy);
      this.gamePermissions = [...(response.policy.permissions || [])];
      this.gameImmunity = response.policy.immunity;
      this.gameRolesMessage = response.message;
    }).catch(error => {
      console.error(error);
      this.gameRolesError = this.getErrorMessage(error, 'Game rank permissions could not be saved.');
    }).finally(() => {
      this.gameRolesSaving = false;
      m.redraw();
    });
  }
  view() {
    if (!this.isOwner()) {
      return m(PageStructure, {
        className: "HideoutStaffPage"
      }, m("div", {
        className: "HideoutStaffContainer"
      }, m("section", {
        className: "HideoutStaffPanel"
      }, m("h1", null, "Access denied"), m("p", null, "Only Founders can manage staff assignments."))));
    }
    return m(PageStructure, {
      className: "HideoutStaffPage"
    }, m("div", {
      className: "HideoutStaffContainer"
    }, m("header", {
      className: "HideoutStaffHeader"
    }, m("span", {
      className: "HideoutEyebrow"
    }, "OWNER CONTROL"), m("h1", null, "Staff Management"), m("p", null, "Manage staff assignments, verify linked identities, and review recent administrative changes.")), this.dashboardOverview(), this.gameRolePolicies(), this.searchPanel(), this.member && this.memberCard(), this.staffRoster(), this.auditHistory()));
  }
  gameRolePolicies() {
    const policies = this.gameRoles?.roles || [];
    const groups = this.filteredPermissionGroups();
    return m("section", {
      className: "HideoutStaffPanel HideoutGameRoles"
    }, m("div", {
      className: "HideoutStaffSectionHeader"
    }, m("div", null, m("span", {
      className: "HideoutSectionEyebrow"
    }, "GAME ACCESS"), m("h2", null, "Rank permissions & immunity"), m("p", null, "Everyone assigned a forum rank inherits its game permissions automatically.")), m(Button, {
      className: "Button",
      icon: "fas fa-sync-alt",
      loading: this.gameRolesLoading,
      disabled: this.gameRolesLoading || this.gameRolesSaving,
      onclick: () => this.loadGameRoles()
    }, "Refresh")), this.gameRolesLoading && !this.gameRoles ? m("div", {
      className: "HideoutDashboardLoading"
    }, m(LoadingIndicator, null)) : this.gameRoles ? m("div", {
      className: "HideoutGameRoleBody"
    }, m("form", {
      className: "HideoutCreateGameRole",
      onsubmit: event => this.createGameRole(event)
    }, m("label", null, m("span", null, "Add a game rank"), m("input", {
      className: "FormControl",
      type: "text",
      maxlength: "40",
      value: this.newGameRole,
      placeholder: "For example: Trial Moderator",
      disabled: this.gameRolesSaving,
      oninput: event => {
        this.newGameRole = event.target.value;
      }
    })), m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.gameRolesSaving,
      disabled: this.gameRolesSaving || !this.newGameRole.trim()
    }, "Add rank")), m("div", {
      className: "HideoutGameRoleTabs"
    }, policies.map(policy => m("button", {
      type: "button",
      key: policy.role,
      className: policy.role === this.selectedGameRole ? 'HideoutGameRoleTab is-active' : 'HideoutGameRoleTab',
      disabled: this.gameRolesSaving,
      onclick: () => this.selectGameRole(policy.role)
    }, m("span", null, policy.role), m("small", null, policy.permissions.length, " permissions \xB7", ' ', policy.immunity, " immunity")))), m("div", {
      className: "HideoutGameRoleEditor"
    }, m("div", {
      className: "HideoutGameRoleEditorHeader"
    }, m("div", null, m("span", {
      className: "HideoutSectionEyebrow"
    }, "EDITING RANK"), m("h3", null, this.selectedGameRole)), m("span", {
      className: "HideoutGameRoleCount"
    }, this.gamePermissions.length, " selected")), m("div", {
      className: "HideoutGameRoleControls"
    }, m("label", {
      className: "HideoutGameImmunity"
    }, m("span", null, "Immunity level"), m("input", {
      className: "FormControl",
      type: "number",
      min: "0",
      max: "255",
      step: "1",
      value: this.gameImmunity,
      disabled: this.gameRolesSaving,
      oninput: event => {
        this.gameImmunity = event.target.value;
        this.gameRolesMessage = null;
      }
    }), m("small", null, "Staff cannot target players with equal or higher immunity. Range: 0\u2013255.")), m("label", {
      className: "HideoutPermissionSearch"
    }, m("span", null, "Find a permission"), m("div", {
      className: "HideoutPermissionSearchControl"
    }, m("i", {
      className: "fas fa-search"
    }), m("input", {
      className: "FormControl",
      type: "search",
      value: this.permissionSearch,
      placeholder: "Search key, label, or plugin",
      disabled: this.gameRolesSaving,
      oninput: event => {
        this.permissionSearch = event.target.value;
      }
    })), m("small", null, "Permission keys come from plugins registered with BanSystem."))), !this.gameRoles.catalogLive && m("div", {
      className: "HideoutGameCatalogNotice"
    }, m("i", {
      className: "fas fa-info-circle"
    }), m("span", null, "The live plugin catalog is unavailable, so the built-in BanSystem keys are shown. You can still add another raw permission below.")), m("div", {
      className: "HideoutPermissionGroups"
    }, groups.map(group => this.permissionGroup(group)), groups.length === 0 && m("div", {
      className: "HideoutPermissionEmpty"
    }, "No permissions match that search.")), this.customPermissions().length > 0 && m("div", {
      className: "HideoutCustomPermissions"
    }, m("span", {
      className: "HideoutPermissionGroupTitle"
    }, "CUSTOM / SAVED"), m("div", {
      className: "HideoutPermissionGrid"
    }, this.customPermissions().map(permission => m("label", {
      className: "HideoutPermissionOption is-selected",
      key: permission
    }, m("input", {
      type: "checkbox",
      checked: true,
      disabled: this.gameRolesSaving,
      onchange: () => this.toggleGamePermission(permission)
    }), m("span", null, m("code", null, permission), m("small", null, "Custom permission key")))))), m("form", {
      className: "HideoutCustomPermissionForm",
      onsubmit: event => this.addCustomGamePermission(event)
    }, m("label", null, m("span", null, "Add a raw permission key"), m("input", {
      className: "FormControl",
      type: "text",
      value: this.customPermission,
      maxlength: "100",
      autocomplete: "off",
      placeholder: "plugin:permission",
      disabled: this.gameRolesSaving,
      oninput: event => {
        this.customPermission = event.target.value;
      }
    })), m(Button, {
      type: "submit",
      className: "Button",
      disabled: this.gameRolesSaving || !this.customPermission.trim()
    }, "Add key")), false && m("div", {
      className: "HideoutStaffAlert HideoutStaffAlert--error"
    }, this.gameRolesError), this.gameRolesMessage && m("div", {
      className: "HideoutStaffAlert HideoutStaffAlert--success"
    }, this.gameRolesMessage, " Running servers refresh staff access within about 40 seconds."), m("div", {
      className: "HideoutGameRoleFooter"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-save",
      loading: this.gameRolesSaving,
      disabled: this.gameRolesSaving,
      onclick: () => this.saveGameRole()
    }, "Save ", this.selectedGameRole), m("span", null, "Applies to", ' ', (this.gameRoles.gameTypes || []).join(', ') || 'source2')))) : m("div", {
      className: "HideoutGameCatalogNotice HideoutGameCatalogNotice--error"
    }, m("i", {
      className: "fas fa-exclamation-circle"
    }), m("span", null, this.gameRolesError || 'Game rank permissions could not be loaded.')));
  }
  filteredPermissionGroups() {
    const query = this.permissionSearch.trim().toLowerCase();
    return (this.gameRoles?.permissionGroups || []).map(group => ({
      ...group,
      permissions: (group.permissions || []).filter(permission => !query || group.plugin.toLowerCase().includes(query) || permission.key.toLowerCase().includes(query) || permission.label.toLowerCase().includes(query))
    })).filter(group => group.permissions.length > 0);
  }
  permissionGroup(group) {
    return m("section", {
      className: "HideoutPermissionGroup",
      key: group.plugin
    }, m("div", {
      className: "HideoutPermissionGroupHeader"
    }, m("span", {
      className: "HideoutPermissionGroupTitle"
    }, group.plugin), m("small", null, group.permissions.length, " available")), m("div", {
      className: "HideoutPermissionGrid"
    }, group.permissions.map(permission => {
      const selected = this.hasGamePermission(permission.key);
      return m("label", {
        className: selected ? 'HideoutPermissionOption is-selected' : 'HideoutPermissionOption',
        key: permission.key
      }, m("input", {
        type: "checkbox",
        checked: selected,
        disabled: this.gameRolesSaving,
        onchange: () => this.toggleGamePermission(permission.key)
      }), m("span", null, m("code", null, permission.key), m("small", null, permission.label, permission.baseType ? ` · ${permission.baseType}` : '')));
    })));
  }
  customPermissions() {
    const catalogKeys = new Set((this.gameRoles?.permissionGroups || []).flatMap(group => (group.permissions || []).map(permission => permission.key)));
    return this.gamePermissions.filter(permission => !catalogKeys.has(permission));
  }
  dashboardOverview() {
    const summary = this.dashboard?.summary;
    const counts = summary?.roleCounts || {};
    return m("section", {
      className: "HideoutStaffOverview"
    }, m("div", {
      className: "HideoutStaffOverviewHeader"
    }, m("div", null, m("span", {
      className: "HideoutSectionEyebrow"
    }, "TEAM STATUS"), m("h2", null, "Staff overview"), m("p", null, "Current assignments across The Hideout.")), m(Button, {
      className: "Button",
      icon: "fas fa-sync-alt",
      loading: this.dashboardLoading,
      disabled: this.dashboardLoading,
      onclick: () => this.loadDashboard()
    }, "Refresh")), false && m("div", {
      className: "HideoutStaffAlert HideoutStaffAlert--error"
    }, this.dashboardError), this.dashboardLoading && !this.dashboard ? m("div", {
      className: "HideoutDashboardLoading"
    }, m(LoadingIndicator, null)) : m("div", {
      className: "HideoutStaffStats"
    }, this.statCard('Total staff', summary?.totalStaff || 0, 'fas fa-users-cog', 'total'), STAFF_ROLES.map(role => this.statCard(role, counts[role] || 0, this.roleIcon(role), this.roleClass(role)))), this.dashboard?.sync && m("div", {
      className: "HideoutDashboardSync"
    }, this.dashboardSyncItem('Forum roles', this.dashboard.sync.forum), this.dashboardSyncItem('Discord roles', this.dashboard.sync.discord), this.dashboardSyncItem('Game servers', this.dashboard.sync.gameServers)));
  }
  statCard(label, value, icon, modifier) {
    return m("div", {
      className: `HideoutStaffStat HideoutStaffStat--${modifier}`
    }, m("div", {
      className: "HideoutStaffStatIcon"
    }, m("i", {
      className: icon
    })), m("div", null, m("strong", null, value), m("span", null, label)));
  }
  dashboardSyncItem(label, active) {
    return m("div", {
      className: active ? 'HideoutDashboardSyncItem HideoutDashboardSyncItem--active' : 'HideoutDashboardSyncItem'
    }, m("i", {
      className: active ? 'fas fa-check-circle' : 'fas fa-clock'
    }), m("span", null, label), m("strong", null, active ? 'Active' : 'Coming soon'));
  }
  roleIcon(role) {
    const icons = {
      Developer: 'fas fa-code',
      'Surf Manager': 'fas fa-water',
      'GMOD Manager': 'fas fa-gamepad',
      'Surf Admin': 'fas fa-shield-alt',
      'GMOD Admin': 'fas fa-user-shield'
    };
    return icons[role] || 'fas fa-user-shield';
  }
  roleClass(role) {
    return role.toLowerCase().replace(/\s+/g, '-');
  }
  searchPanel() {
    return m("section", {
      className: "HideoutStaffPanel"
    }, m("div", {
      className: "HideoutStaffSectionHeader"
    }, m("div", null, m("span", {
      className: "HideoutSectionEyebrow"
    }, "MEMBER LOOKUP"), m("h2", null, "Find a verified member"), m("p", null, "Both values must match the same linked Hideout account.")), this.member && m(Button, {
      className: "Button",
      disabled: this.searching || this.saving,
      onclick: () => this.clearMember()
    }, "Clear result")), m("form", {
      onsubmit: event => this.lookupMember(event)
    }, m("div", {
      className: "HideoutStaffFormGrid"
    }, m("label", null, m("span", null, "Exact forum username"), m("input", {
      className: "FormControl",
      type: "text",
      value: this.username,
      autocomplete: "off",
      placeholder: "Forum username",
      disabled: this.searching || this.saving,
      oninput: event => {
        this.username = event.target.value;
      }
    })), m("label", null, m("span", null, "Verified SteamID64"), m("input", {
      className: "FormControl",
      type: "text",
      inputmode: "numeric",
      maxlength: "17",
      value: this.steamId64,
      autocomplete: "off",
      placeholder: "7656119...",
      disabled: this.searching || this.saving,
      oninput: event => {
        this.steamId64 = event.target.value.replace(/\D/g, '');
      }
    }))), m(Button, {
      type: "submit",
      className: "Button Button--primary",
      loading: this.searching,
      disabled: this.saving || !this.username.trim() || this.steamId64.trim().length !== 17
    }, "Verify member")), false && m("div", {
      className: "HideoutStaffAlert HideoutStaffAlert--error"
    }, this.error), this.message && m("div", {
      className: "HideoutStaffAlert HideoutStaffAlert--success"
    }, this.message), this.syncStatus && this.syncStatusPanel());
  }
  syncStatusPanel() {
    return m("div", {
      className: "HideoutSyncStatus"
    }, m("h3", null, "Synchronization status"), m("div", {
      className: "HideoutSyncStatusGrid"
    }, this.syncItem('Forum role', this.syncStatus.forum, 'Updated'), this.syncItem('Game servers', this.syncStatus.gameServers, 'Not configured'), this.syncItem('Discord role', this.syncStatus.discord, 'Not configured')));
  }
  syncItem(label, synced, successText) {
    return m("div", {
      className: synced ? 'HideoutSyncItem HideoutSyncItem--success' : 'HideoutSyncItem'
    }, m("i", {
      className: synced ? 'fas fa-check-circle' : 'fas fa-minus-circle'
    }), m("div", null, m("strong", null, label), m("span", null, synced ? successText : 'Not configured')));
  }
  memberCard() {
    const steam = this.member.steam;
    const discord = this.member.discord;
    const discordName = discord?.globalName || discord?.username || 'Discord account';
    return m("section", {
      className: "HideoutStaffPanel HideoutVerifiedMember"
    }, m("div", {
      className: "HideoutVerifiedHeader"
    }, m("div", null, m("span", {
      className: "HideoutVerifiedBadge"
    }, m("i", {
      className: "fas fa-check"
    }), " Identity verified"), m("h2", null, this.member.displayName), m("p", null, "@", this.member.username)), m("div", {
      className: "HideoutVerifiedLinks"
    }, m("a", {
      className: "Button",
      href: steam.profileUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("i", {
      className: "fab fa-steam"
    }), " Steam profile"), discord?.connected && discord.profileUrl && m("a", {
      className: "Button",
      href: discord.profileUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("i", {
      className: "fab fa-discord"
    }), " Discord profile"))), m("div", {
      className: "HideoutMemberData"
    }, m("div", null, m("span", {
      className: "HideoutMemberDataLabel"
    }, "Forum account"), m("strong", null, this.member.username)), m("div", null, m("span", {
      className: "HideoutMemberDataLabel"
    }, "SteamID64"), m("strong", null, steam.steamId64), m("span", {
      className: "HideoutIdentityStatus HideoutIdentityStatus--verified"
    }, m("i", {
      className: "fas fa-check-circle"
    }), " Verified")), m("div", null, m("span", {
      className: "HideoutMemberDataLabel"
    }, "Discord"), discord?.connected ? m('[', null, m("strong", null, discordName), discord.username && discord.globalName && discord.username !== discord.globalName && m("span", null, "@", discord.username), m("span", {
      className: "HideoutIdentityStatus HideoutIdentityStatus--verified"
    }, m("i", {
      className: "fas fa-check-circle"
    }), " Connected")) : m("span", {
      className: "HideoutIdentityStatus"
    }, m("i", {
      className: "fas fa-minus-circle"
    }), " Not connected")), m("div", null, m("span", {
      className: "HideoutMemberDataLabel"
    }, "Current staff role"), m("strong", null, this.member.currentRole || 'None'))), m("hr", null), m("div", {
      className: "HideoutStaffChangeGrid"
    }, m("label", {
      className: "HideoutRoleSelect"
    }, m("span", null, "New staff role"), m("select", {
      className: "FormControl",
      value: this.selectedRole,
      disabled: this.saving,
      onchange: event => {
        this.selectedRole = event.target.value;
        this.clearAlerts();
      }
    }, STAFF_ROLES.map(role => m("option", {
      value: role
    }, role)))), m("label", {
      className: "HideoutStaffReason"
    }, m("span", null, "Reason for this change"), m("textarea", {
      className: "FormControl",
      rows: "4",
      maxlength: "500",
      value: this.reason,
      disabled: this.saving,
      placeholder: "Required \u2014 explain why this staff change is being made.",
      oninput: event => {
        this.reason = event.target.value;
        this.clearAlerts();
      }
    }), m("small", null, this.reason.trim().length, "/500 characters", this.reason.trim().length < 5 ? ' — at least 5 required' : ''))), m("div", {
      className: "HideoutStaffActions"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.saving,
      disabled: this.saving || this.reason.trim().length < 5 || this.selectedRole === this.member.currentRole,
      onclick: () => this.assignRole()
    }, this.member.currentRole ? 'Change staff role' : 'Grant selected role'), m(Button, {
      className: "Button Button--danger",
      loading: this.saving,
      disabled: this.saving || !this.member.currentRole || this.reason.trim().length < 5,
      onclick: () => this.revokeRole()
    }, "Remove staff role")), m("p", {
      className: "HideoutAuditNotice"
    }, "Every change records the Founder, member, verified SteamID64, previous role, new role, reason, and timestamp."));
  }
  staffRoster() {
    if (!this.dashboard) {
      return null;
    }
    const staff = this.dashboard.staff || [];
    return m("section", {
      className: "HideoutStaffPanel HideoutRosterPanel"
    }, m("div", {
      className: "HideoutStaffSectionHeader"
    }, m("div", null, m("span", {
      className: "HideoutSectionEyebrow"
    }, "ACTIVE TEAM"), m("h2", null, "Staff roster"), m("p", null, "Verified identities and current staff access.")), m("span", {
      className: "HideoutRosterCount"
    }, staff.length, ' ', staff.length === 1 ? 'member' : 'members')), staff.length === 0 ? m("div", {
      className: "HideoutDashboardEmpty"
    }, m("i", {
      className: "fas fa-user-shield"
    }), m("h3", null, "No assigned staff yet"), m("p", null, "Verify a member above to grant the first staff role.")) : m("div", {
      className: "HideoutRosterGrid"
    }, staff.map(member => this.rosterCard(member))));
  }
  rosterCard(member) {
    const discordName = member.discord?.globalName || member.discord?.username || 'Not connected';
    return m("article", {
      className: "HideoutRosterCard",
      key: member.id
    }, m("div", {
      className: "HideoutRosterCardHeader"
    }, member.avatarUrl ? m("img", {
      className: "HideoutRosterAvatar",
      src: member.avatarUrl,
      alt: ""
    }) : m("span", {
      className: "HideoutRosterAvatar HideoutRosterAvatar--fallback"
    }, member.username.charAt(0).toUpperCase()), m("div", {
      className: "HideoutRosterIdentity"
    }, m("strong", null, member.displayName), m("span", null, "@", member.username)), m("span", {
      className: `HideoutRoleBadge HideoutRoleBadge--${this.roleClass(member.role)}`
    }, member.role)), m("div", {
      className: "HideoutRosterConnections"
    }, m("div", null, m("i", {
      className: "fab fa-steam"
    }), m("span", null, member.steam?.connected ? member.steam.steamId64 : 'Not connected'), m("i", {
      className: member.steam?.connected ? 'fas fa-check-circle HideoutConnectionGood' : 'fas fa-minus-circle'
    })), m("div", null, m("i", {
      className: "fab fa-discord"
    }), m("span", null, discordName), m("i", {
      className: member.discord?.connected ? 'fas fa-check-circle HideoutConnectionGood' : 'fas fa-minus-circle'
    }))), m("div", {
      className: "HideoutRosterMeta"
    }, m("span", null, "Granted by ", m("strong", null, member.grantedBy)), m("span", null, this.formatDate(member.updatedAt || member.grantedAt))), m("div", {
      className: "HideoutRosterActions"
    }, m("a", {
      className: "Button",
      href: member.forumProfileUrl
    }, m("i", {
      className: "fas fa-user"
    }), " Forum profile"), member.steam?.profileUrl && m("a", {
      className: "Button",
      href: member.steam.profileUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("i", {
      className: "fab fa-steam"
    }), " Steam"), m(Button, {
      className: "Button Button--primary",
      onclick: () => this.manageRosterMember(member)
    }, "Manage")));
  }
  manageRosterMember(member) {
    this.username = member.username;
    this.steamId64 = member.steam?.steamId64 || '';
    this.selectedRole = member.role || 'Surf Admin';
    this.reason = '';
    this.clearAlerts();
    if (this.steamId64.length === 17) {
      this.lookupMember({
        preventDefault() {}
      });
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  auditHistory() {
    if (!this.dashboard) {
      return null;
    }
    const audit = this.dashboard.audit || [];
    return m("section", {
      className: "HideoutStaffPanel HideoutAuditPanel"
    }, m("div", {
      className: "HideoutStaffSectionHeader"
    }, m("div", null, m("span", {
      className: "HideoutSectionEyebrow"
    }, "ACCOUNTABILITY"), m("h2", null, "Recent staff changes"), m("p", null, "The latest 50 recorded assignment events.")), m("span", {
      className: "HideoutAuditCount"
    }, audit.length, " shown")), audit.length === 0 ? m("div", {
      className: "HideoutDashboardEmpty"
    }, m("i", {
      className: "fas fa-clipboard-list"
    }), m("h3", null, "No staff changes recorded")) : m("div", {
      className: "HideoutAuditList"
    }, audit.map(entry => this.auditEntry(entry))));
  }
  auditEntry(entry) {
    const assigned = entry.action === 'assign';
    return m("article", {
      className: "HideoutAuditEntry",
      key: entry.id
    }, m("div", {
      className: assigned ? 'HideoutAuditIcon HideoutAuditIcon--assign' : 'HideoutAuditIcon HideoutAuditIcon--revoke'
    }, m("i", {
      className: assigned ? 'fas fa-user-plus' : 'fas fa-user-minus'
    })), m("div", {
      className: "HideoutAuditBody"
    }, m("div", {
      className: "HideoutAuditTitle"
    }, m("strong", null, entry.actor.username), m("span", null, assigned ? 'changed' : 'removed'), m("strong", null, entry.target.username)), m("div", {
      className: "HideoutAuditRoleChange"
    }, m("span", null, entry.oldRole || 'No staff role'), m("i", {
      className: "fas fa-arrow-right"
    }), m("span", null, entry.newRole || 'No staff role')), m("p", null, entry.reason), m("div", {
      className: "HideoutAuditMeta"
    }, m("span", null, m("i", {
      className: "fab fa-steam"
    }), ' ', entry.steamId64), m("time", null, this.formatDate(entry.createdAt)))));
  }
  formatDate(value) {
    if (!value) {
      return 'Unknown date';
    }
    const normalized = String(value).includes('T') ? value : `${value} UTC`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }
}
flarum.reg.add('thehideout-core', 'forum/components/StaffManagementPage', StaffManagementPage);